import { RAW_PAX_CREDITS_PER_DISPLAY_CREDIT } from "@pax/api-contract/accounting/v1alpha1";
import { MAX_STAFF_PAY_RAW } from "@pax/api-contract/staff/v1alpha1";

/** Pay as it is being typed: an amount of Pax credits a month, or null for unpaid. */
export type PayDraft = string | null;

const RAW_PER_HUNDREDTH = RAW_PAX_CREDITS_PER_DISPLAY_CREDIT / 100n;

function creditsFromRaw(raw: string): string {
  const hundredths = BigInt(raw) / RAW_PER_HUNDREDTH;
  const whole = hundredths / 100n;
  const cents = (hundredths % 100n).toString().padStart(2, "0").replace(/0+$/, "");
  return cents ? `${whole}.${cents}` : whole.toString();
}

/** "250", in the reader's number format. */
export const creditsAmount = (pay: string): string =>
  Number(creditsFromRaw(pay)).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** "credit" for exactly one, else "credits". */
export const creditsUnit = (pay: string): string =>
  creditsFromRaw(pay) === "1" ? "credit" : "credits";

/** "250 credits a month", or "Unpaid". */
export const payLabel = (pay: string | null): string =>
  pay ? `${creditsAmount(pay)} ${creditsUnit(pay)} a month` : "Unpaid";

/** The default pay of several roles added together, or null when none of them has any. */
export const defaultPayOf = (roles: readonly { defaultPay: string | null }[]): string | null =>
  roles.reduce<string | null>((total, role) => addPay(total, role.defaultPay), null);

export const draftFromPay = (pay: string | null): PayDraft => pay && creditsFromRaw(pay);

/** The raw pay a draft describes, or undefined while it is not a positive number of credits with at most two decimals. */
export function payFromDraft(draft: PayDraft): string | null | undefined {
  if (draft === null) return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(draft.trim());
  if (!match) return undefined;
  const hundredths = BigInt(match[1] ?? "0") * 100n + BigInt((match[2] ?? "").padEnd(2, "0"));
  if (hundredths === 0n) return undefined;
  return (hundredths * RAW_PER_HUNDREDTH).toString();
}

export const withinPayCap = (pay: string | null) => !pay || BigInt(pay) <= MAX_STAFF_PAY_RAW;

/** Both pays added together, as one would be paid. */
export function addPay(left: string | null, right: string | null): string | null {
  if (!left || !right) return left ?? right;
  return (BigInt(left) + BigInt(right)).toString();
}

/** Today in UTC, written `YYYY-MM-DD`. */
export const todayUtc = () => new Date().toISOString().slice(0, 10);

/** Today in UTC plus one month. A date past the end of next month moves back, so January 31 becomes February 28. */
export function oneMonthFromToday(): string {
  const date = new Date(`${todayUtc()}T00:00:00Z`);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + 1);
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return date.toISOString().slice(0, 10);
}

/** A pay date as "Oct 10", with the year when it is not this year. */
export function payDateLabel(date: string): string {
  const day = new Date(`${date}T00:00:00Z`);
  return day.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: day.getUTCFullYear() === new Date().getUTCFullYear() ? undefined : "numeric",
    timeZone: "UTC",
  });
}

/** How far a pay date is from today: "today", "in 23 days", "4 days ago". */
export function payDateDistance(date: string): string {
  const days = Math.round(
    (Date.parse(`${date}T00:00:00Z`) - Date.parse(`${todayUtc()}T00:00:00Z`)) / 86_400_000,
  );
  return new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(days, "day");
}
